import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cssUrl } from '../utils/cssVersion';
import { useUser } from '../utils/UserContext';
import { useMessages } from '../utils/MessagesContext';
import { getRoleAccent } from '../data/map';

/**
 * MessageCenter — the global Messages modal + dock pill.
 * Rendered once from the app root (inside MessagesProvider); it portals to
 * <body> so it floats over every page and survives route changes.
 *
 *   panel 'open'    → modal: Inbox / Thread / Compose
 *   panel 'docked'  → pill bottom-right ("Messages · 3"), tap to reopen
 *   panel 'closed'  → nothing
 */

const ORG_LABEL  = { snl: 'SNL', iglu: 'IGLU', client: 'Clients' };
const TIER_LABEL = { all: 'Everyone', subcontractor: 'Subcontractors', employee: 'Employees', supervisor: 'Supervisors', management: 'Management', iglu: 'IGLU South Yarra' };
const TIER_ORDER = ['all', 'subcontractor', 'employee', 'supervisor', 'management'];

const PREVIEW_WORDS = 15;
const TOAST_MEMORY = 200;               // message ids remembered per user, so the list can't grow forever
const preview = (text = '') => {
  const words = String(text).replace(/\s+/g, ' ').trim().split(' ');
  return words.length > PREVIEW_WORDS ? `${words.slice(0, PREVIEW_WORDS).join(' ')}…` : words.join(' ');
};
const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';
const roleOf = (p) => {
  if (!p) return '';
  if (p.org === 'client') return p.job || 'Client';
  if (p.org === 'iglu') return 'IGLU South Yarra';
  return p.job || TIER_LABEL[p.tier] || '';
};
const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso), now = new Date();
  const same = d.toDateString() === now.toDateString();
  const t = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  if (same) return t;
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return `Yesterday ${t}`;
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) + ' ' + t;
};

const useStylesheet = (href) => {
  useEffect(() => {
    const key = href.split('?')[0];
    if (document.querySelector(`link[data-mc-css="${key}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = href; link.dataset.mcCss = key;
    document.head.appendChild(link);
  }, [href]);
};

const Avatar = ({ name, photo, accent, size = 36 }) => (
  <span className="mc-avatar" style={{ width: size, height: size, fontSize: size * 0.38, background: `${accent}22`, color: accent, borderColor: `${accent}55` }} title={name}>
    {photo ? <img src={photo} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null}
    <span className="mc-avatar-txt">{initials(name)}</span>
  </span>
);

const GroupAvatar = ({ count, accent, size = 36 }) => (
  <span className="mc-avatar mc-avatar-group" style={{ width: size, height: size, fontSize: size * 0.3, background: `${accent}22`, color: accent, borderColor: `${accent}55` }} title={`${count} people`}>
    <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
  </span>
);
// role 'viewer' = management oversight (IT Operations / MD / CEO) looking at a thread they are not part of — read-only, see the server
const partiesOf = (t) => `${t.other?.name || ''} → ${(t.recipients || []).slice(0, 3).map((p) => p.name).join(', ')}${t.recipientCount > 3 ? ` +${t.recipientCount - 3}` : ''}`;
const whoLine = (t) => {
  if (t.role === 'viewer') return partiesOf(t);
  if (t.group) return `To ${t.recipientCount} people`;
  if (t.role === 'recipient') return `From ${t.other?.name || ''}`;
  return `To ${t.other?.name || ''}`;
};

/* ─────────────────────────────────────────────────────────────
   Inbox
   ───────────────────────────────────────────────────────────── */
const Inbox = ({ accent }) => {
  const { threads, threadsLoading, threadsLoaded, openThread, startCompose } = useMessages();
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? threads.filter((t) => (t.title || '').toLowerCase().includes(n) || (t.other?.name || '').toLowerCase().includes(n) || (t.recipients || []).some((p) => p.name.toLowerCase().includes(n)) || t.lastText.toLowerCase().includes(n)) : threads;
  }, [threads, q]);
  return (
    <div className="mc-inbox">
      <div className="mc-toolbar">
        <input className="mc-search" placeholder="Search conversations" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" className="mc-btn mc-btn-solid" style={{ background: accent }} onClick={() => startCompose()}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
          New message
        </button>
      </div>
      {threadsLoading && !threadsLoaded && <div className="mc-skeleton" />}
      {threadsLoaded && list.length === 0 && (
        <div className="mc-empty">
          <div className="mc-empty-title">{q ? 'No conversations match' : 'No messages yet'}</div>
          <p>{q ? 'Try another name.' : 'Start one with New message — pick a person or a whole group.'}</p>
        </div>
      )}
      {threadsLoaded && list.length > 0 && (
        <div className="mc-threads-head">
          <span>{list.length} conversation{list.length === 1 ? '' : 's'}</span>
        </div>
      )}
      <div className="mc-threads">
        {list.map((t) => {
          const mine = !!t.messages[t.messages.length - 1]?.fromMe;   // fromMe is computed server-side on the login record id
          return (
            <button type="button" key={t.key} className={`mc-thread ${t.unread ? 'mc-unread' : 'mc-seen'}`} style={t.unread ? { borderLeftColor: accent } : undefined} onClick={() => openThread(t.key)}>
              {t.group ? <GroupAvatar count={t.recipientCount} accent={accent} /> : <Avatar name={t.other?.name} photo={t.other?.photo} accent={accent} />}
              <span className="mc-thread-main">
                <span className="mc-thread-top">
                  <span className="mc-thread-title">{t.title || 'Message'}</span>
                  <span className="mc-thread-when">{when(t.lastAt)}</span>
                </span>
                <span className="mc-thread-name">{whoLine(t)}{!t.group && roleOf(t.other) && <span className="mc-thread-role"> · {roleOf(t.other)}</span>}<span className="mc-thread-count"> · {t.messages.length} message{t.messages.length === 1 ? '' : 's'}</span></span>
                <span className="mc-thread-last">{mine ? 'You: ' : (t.group ? `${t.lastFromName}: ` : '')}{preview(t.lastText)}</span>
                <span className="mc-thread-foot">
                  {t.unread > 0
                    ? <span className="mc-pill mc-pill-new" style={{ background: accent }}>New{t.unread > 1 ? ` · ${t.unread}` : ''}</span>
                    : <span className="mc-pill mc-pill-seen">Seen</span>}
                  <span className="mc-thread-open">Tap to read and reply ›</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Thread
   ───────────────────────────────────────────────────────────── */
const Thread = ({ accent }) => {
  const { activeThread: t, reply, goInbox, threadDrafts, setThreadDrafts, setError } = useMessages();
  const [busy, setBusy] = useState(false);
  const [inline, setInline] = useState(null);            // { id, email, name } — owner replying under one bubble
  const [inlineText, setInlineText] = useState('');
  const [inlineBusy, setInlineBusy] = useState(false);
  const [info, setInfo] = useState(null);                // recipient shown in the ⓘ card
  const text = (t && threadDrafts[t.key]) || '';
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [t?.messages?.length]);
  if (!t) return <div className="mc-empty"><div className="mc-empty-title">Conversation not found</div><button type="button" className="mc-btn" onClick={goInbox}>Back to inbox</button></div>;

  const submit = async () => {
    const v = text.trim();
    if (!v || busy) return;
    setBusy(true);
    try { await reply(t.key, v, ''); setThreadDrafts((d) => ({ ...d, [t.key]: '' })); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const sendInline = async () => {
    const v = inlineText.trim();
    if (!v || !inline || inlineBusy) return;
    setInlineBusy(true);
    try { await reply(t.key, v, inline.personId || ''); setInlineText(''); setInline(null); }
    catch (e) { setError(e.message); }
    finally { setInlineBusy(false); }
  };
  const personOf = (id) => (t.recipients || []).find((p) => p.id === id) || null;

  return (
    <div className="mc-threadview">
      <div className="mc-thread-head">
        <button type="button" className="mc-icon-btn" aria-label="Back" onClick={goInbox}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        {t.group ? <GroupAvatar count={t.recipientCount} accent={accent} size={34} /> : <Avatar name={t.other?.name} photo={t.other?.photo} accent={accent} size={34} />}
        <div className="mc-thread-who">
          <div className="mc-thread-title">{t.title || 'Message'}</div>
          {t.role === 'viewer'
            ? <div className="mc-thread-role"><b>{partiesOf(t)}</b> · Management view</div>
            : t.group
            ? <div className="mc-thread-role"><b>To {t.recipientCount} people</b> · {t.recipients.slice(0, 4).map((p) => p.name).join(', ')}{t.recipientCount > 4 ? ` +${t.recipientCount - 4} more` : ''}</div>
            : <div className="mc-thread-role"><b>{t.other?.name}</b>{roleOf(t.other) && <> · {roleOf(t.other)}</>}{t.other?.mobile && <> · {t.other.mobile}</>}</div>}
        </div>
      </div>
      <div className="mc-bubbles">
        {t.messages.map((m) => {
          const mine = m.fromMe;                          // server-computed: fromId === my login record id
          const toAll = !m.toId;                          // To User is the link; To Email can be dropped by the 422 retry in _post
          // labels: owner of a group thread sees who → whom on every bubble; others only need "to everyone" on broadcasts
          const label = t.role === 'viewer'
            ? `${m.fromName} → ${toAll ? 'everyone' : (m.toName || '')}`          // a viewer follows who spoke to whom
            : t.group
            ? (mine ? `You → ${toAll ? 'everyone' : m.toName}` : `${m.fromName} → you`)
            : (toAll && !mine && t.recipientCount > 1 ? 'To everyone' : '');
          const seenNote = mine
            ? (t.group && toAll ? ` · Seen by ${m.seenCount}/${t.recipientCount}` : (m.seenCount > 0 ? ' · Seen' : ''))
            : '';
          const canInline = t.group && !mine && t.role !== 'viewer';
          return (
            <div key={m.id} className="mc-bubble-block">
              <div className={`mc-bubble-row ${mine ? 'mine' : ''}`}>
                {!mine && t.group && <Avatar name={m.fromName} photo={m.fromPhoto} accent={accent} size={26} />}
                <div className="mc-bubble" style={mine ? { background: `${accent}2a`, borderColor: `${accent}55` } : undefined}>
                  {label && <span className="mc-audience">{label}</span>}
                  <div className="mc-bubble-text">{m.text}</div>
                  <div className="mc-bubble-meta">{when(m.createdAt)}{seenNote}</div>
                  {canInline && (
                    <div className="mc-bubble-actions">
                      {(() => {
                        const open = inline?.id === m.id;
                        const hasText = open && inlineText.trim().length > 0;
                        const caption = inlineBusy && open ? 'Sending…' : hasText ? 'Send' : 'Reply to';
                        const onClick = () => {
                          if (!open) { setInline({ id: m.id, personId: m.fromId, name: m.fromName }); setInlineText(''); return; }
                          if (hasText) { sendInline(); return; }
                          setInline(null);                       // open + empty → close it again
                        };
                        return (
                          <button type="button" className={`mc-mini ${hasText ? 'mc-mini-solid' : ''}`} disabled={inlineBusy && open}
                                  style={hasText ? { background: accent, borderColor: accent, color: '#0b1220' } : { color: accent, borderColor: `${accent}66` }}
                                  onClick={onClick}>{caption}</button>
                        );
                      })()}
                      <button type="button" className="mc-mini" title="Details" onClick={() => setInfo(info?.id === m.fromId ? null : (personOf(m.fromId) || { id: m.fromId, name: m.fromName, email: m.fromEmail, photo: m.fromPhoto }))}>ⓘ</button>
                    </div>
                  )}
                </div>
              </div>
              {info && canInline && info.id === m.fromId && (
                <div className="mc-info-card">
                  <Avatar name={info.name} photo={info.photo} accent={accent} size={44} />
                  <div className="mc-info-main">
                    <div className="mc-info-name">{info.name}</div>
                    <div className="mc-info-line">{roleOf(info) || '—'}</div>
                    {info.mobile && <div className="mc-info-line">{info.mobile}</div>}
                    <div className="mc-info-line">{info.email}</div>
                  </div>
                  <button type="button" className="mc-mini" onClick={() => setInfo(null)}>Close</button>
                </div>
              )}
              {inline?.id === m.id && (
                <div className="mc-inline">
                  <div className="mc-inline-hint">To <b>{inline.name}</b> only — the button above turns into Send once you type</div>
                  <div className="mc-inline-row">
                    <textarea rows={2} autoFocus placeholder={`Write to ${inline.name}…`} value={inlineText}
                              onChange={(e) => setInlineText(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendInline(); }} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {t.role === 'viewer' ? (
        <div className="mc-reply-hint">Management view — you're not part of this conversation, so there's nothing to reply to here.</div>
      ) : (<>
      <div className="mc-reply-hint">
        {t.group ? <>Message <b>everyone</b> in this conversation ({t.recipientCount}) — use <b>Reply</b> under a message to answer one person</> : <>Replying to <b>{t.other?.name}</b></>}
      </div>
      <div className="mc-reply">
        <textarea rows={2} placeholder={t.group ? `Write a message to all ${t.recipientCount}…` : `Write your reply to ${t.other?.name}…`} value={text}
                  onChange={(e) => setThreadDrafts((d) => ({ ...d, [t.key]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }} />
        <button type="button" className="mc-btn mc-btn-solid" style={{ background: accent }} disabled={busy || !text.trim()} onClick={submit}>{busy ? 'Sending…' : 'Send'}</button>
      </div>
      </>)}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Compose — org rail → tier chips → search / pick → message
   ───────────────────────────────────────────────────────────── */
const Compose = ({ accent }) => {
  const { people, peopleLoading, loadPeople, canBroadcast, me, draft, setDraft, send, goInbox, openThread, setError } = useMessages();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  useEffect(() => { loadPeople(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // `people` is what the server says THIS user may message (SNL sub/employee → SNL only;
  // clients & IGLU → SNL supervisors/management only; supervisors/management → everyone)
  const others = useMemo(() => people.filter((p) => p.id !== me?.id), [people, me]);
  const orgs = useMemo(() => ['snl', 'iglu', 'client'].filter((o) => others.some((p) => p.org === o)), [others]);
  const org = draft.org && orgs.includes(draft.org) ? draft.org : (orgs.includes(me?.org) ? me.org : orgs[0]);

  const tiers = useMemo(() => {
    if (org === 'snl') {
      const present = TIER_ORDER.filter((id) => id !== 'all' && others.some((p) => p.org === 'snl' && p.tier === id));
      // a single tier needs no chips beyond "Everyone" (e.g. clients only ever see managers → 2 chips)
      return [{ id: 'all', label: present.length > 1 ? 'Everyone' : TIER_LABEL[present[0]] || 'Everyone' }, ...(present.length > 1 ? present.map((id) => ({ id, label: TIER_LABEL[id] })) : [])];
    }
    if (org === 'iglu') return [{ id: 'all', label: 'Everyone at IGLU' }];
    const names = [...new Set(others.filter((p) => p.org === 'client').map((p) => p.tier))].sort();
    return [{ id: 'all', label: 'All clients' }, ...names.map((n) => ({ id: n, label: n }))];
  }, [org, others]);
  const tier = tiers.some((t) => t.id === draft.tier) ? draft.tier : 'all';

  const pool = useMemo(() => others.filter((p) => p.org === org && (tier === 'all' || p.tier === tier)), [others, org, tier]);
  const shown = useMemo(() => {
    const n = draft.search.trim().toLowerCase();
    return n ? pool.filter((p) => p.name.toLowerCase().includes(n) || roleOf(p).toLowerCase().includes(n) || p.email.includes(n)) : pool;
  }, [pool, draft.search]);

  // draft.picked holds login RECORD IDS — the server takes either an id or an email
  // (loginportal.send_messages), and ids can't be changed or shared the way an address can.
  const pickedSet = useMemo(() => new Set(draft.picked), [draft.picked]);
  const recipients = draft.selectAll ? pool.map((p) => p.id) : draft.picked;
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setOrg = (o) => set({ org: o, tier: 'all', picked: [], selectAll: false, search: '' });
  const setTier = (t) => set({ tier: t, picked: [], selectAll: false, search: '' });
  const togglePick = (id) => set({ selectAll: false, picked: pickedSet.has(id) ? draft.picked.filter((x) => x !== id) : [...draft.picked, id] });

  const tierLabel = tiers.find((t) => t.id === tier)?.label || '';
  const audience = draft.selectAll
    ? (org === 'snl' ? `all SNL ${tier === 'all' ? 'staff' : tierLabel.toLowerCase()}` : org === 'iglu' ? 'everyone at IGLU' : tier === 'all' ? 'all clients' : `everyone at ${tierLabel}`) + ` (${pool.length})`
    : '';

  const title = draft.title.trim();
  const submit = async () => {
    const text = draft.text.trim();
    if (!text || !title || !recipients.length || busy) return;
    setBusy(true);
    try {
      const r = await send(recipients, text, audience, title);
      setSent(r.sent);
      set({ picked: [], selectAll: false, title: '', text: '', search: '' });
      if (r.threadId) openThread(r.threadId);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const pickedPeople = draft.picked.map((id) => others.find((p) => p.id === id)).filter(Boolean);

  return (
    <div className="mc-compose">
      {/* org rail — vertical on desktop, segmented on phones */}
      <nav className="mc-rail" aria-label="Who to message">
        {orgs.map((o) => (
          <button type="button" key={o} className={`mc-rail-btn ${org === o ? 'on' : ''}`} style={org === o ? { borderColor: accent, color: accent, background: `${accent}14` } : undefined} onClick={() => setOrg(o)}>
            <span className="mc-rail-label">{ORG_LABEL[o]}</span>
            <span className="mc-rail-count">{others.filter((p) => p.org === o).length}</span>
          </button>
        ))}
      </nav>

      <div className="mc-compose-main">
        <div className="mc-chips">
          {tiers.map((t) => (
            <button type="button" key={t.id} className={`mc-chip ${tier === t.id ? 'on' : ''}`} style={tier === t.id ? { borderColor: accent, color: accent } : undefined} onClick={() => setTier(t.id)}>
              {t.label}<span className="mc-chip-n">{others.filter((p) => p.org === org && (t.id === 'all' || p.tier === t.id)).length}</span>
            </button>
          ))}
        </div>

        <div className="mc-pickbar">
          <input className="mc-search" placeholder={`Search ${pool.length} ${org === 'snl' ? (tier === 'all' && tiers.length > 1 ? 'SNL staff' : tierLabel.toLowerCase()) : tierLabel.toLowerCase()}…`} value={draft.search} onChange={(e) => set({ search: e.target.value })} />
          {canBroadcast && pool.length > 1 && (
            <button type="button" className={`mc-chip mc-selectall ${draft.selectAll ? 'on' : ''}`} style={draft.selectAll ? { borderColor: accent, color: accent } : undefined} onClick={() => set({ selectAll: !draft.selectAll, picked: [] })}>
              {draft.selectAll ? `All ${pool.length} selected` : `Select all ${pool.length}`}
            </button>
          )}
        </div>

        {(draft.selectAll || pickedPeople.length > 0) && (
          <div className="mc-tokens">
            {draft.selectAll ? (
              <span className="mc-token mc-token-all" style={{ borderColor: `${accent}66`, color: accent }}>
                Everyone in {org === 'snl' ? `SNL · ${tierLabel}` : tierLabel} ({pool.length})
                <button type="button" aria-label="Clear" onClick={() => set({ selectAll: false })}>×</button>
              </span>
            ) : (
              <>
                {pickedPeople.slice(0, 8).map((p) => (
                  <span key={p.id} className="mc-token">{p.name}<button type="button" aria-label={`Remove ${p.name}`} onClick={() => togglePick(p.id)}>×</button></span>
                ))}
                {pickedPeople.length > 8 && <span className="mc-token mc-token-more">+{pickedPeople.length - 8} more</span>}
                <button type="button" className="mc-link" onClick={() => set({ picked: [] })}>Clear</button>
              </>
            )}
          </div>
        )}

        <div className="mc-people" role="listbox" aria-multiselectable="true">
          {peopleLoading && people.length === 0 && <div className="mc-skeleton" />}
          {!peopleLoading && shown.length === 0 && <div className="mc-empty small">No one matches.</div>}
          {shown.map((p) => {
            const on = draft.selectAll || pickedSet.has(p.id);
            return (
              <button type="button" key={p.id} role="option" aria-selected={on} className={`mc-person ${on ? 'on' : ''}`} onClick={() => togglePick(p.id)} disabled={draft.selectAll}>
                <span className="mc-check" style={on ? { background: accent, borderColor: accent } : undefined}>
                  {on && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0b1220" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                </span>
                <Avatar name={p.name} photo={p.photo} accent={accent} size={30} />
                <span className="mc-person-main">
                  <span className="mc-person-name">{p.name}</span>
                  <span className="mc-person-role">{roleOf(p)}{p.mobile ? ` · ${p.mobile}` : ''}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="mc-composer">
          <input className="mc-title-input" maxLength={120} placeholder="Title — what is this message about? (shown on the card, stays for the whole conversation)" value={draft.title} onChange={(e) => set({ title: e.target.value })} />
          <textarea rows={3} placeholder={recipients.length ? `Message to ${draft.selectAll ? audience : recipients.length === 1 ? pickedPeople[0]?.name : `${recipients.length} people`}…` : 'Pick who to message first'} value={draft.text} onChange={(e) => set({ text: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }} />
          <div className="mc-composer-foot">
            <span className="mc-muted">{recipients.length ? `${recipients.length} recipient${recipients.length === 1 ? '' : 's'}` : ''}{sent != null && !recipients.length ? `Sent to ${sent}` : ''}</span>
            <div className="mc-composer-btns">
              <button type="button" className="mc-btn" onClick={goInbox}>Inbox</button>
              <button type="button" className="mc-btn mc-btn-solid" style={{ background: accent }} disabled={busy || !draft.text.trim() || !title || !recipients.length} onClick={submit} title={!title ? 'Add a title first' : undefined}>{busy ? 'Sending…' : recipients.length > 1 ? `Send to ${recipients.length}` : 'Send'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Session expired
   The bell polls every 30 s on every TabBar page and on /profile, so it is the first
   thing to notice that Flask has dropped the session. Everything else in the app is
   401ing too at that point, so we block rather than let people keep typing into forms
   that can't submit. Deliberately NOT an automatic redirect: this fires from a
   background poll, so a silent navigation would bin whatever they were part-way
   through with no explanation. They click, then we go — which also clears the server
   session, because /index is served by index() and that pops myuser_Data.
   ───────────────────────────────────────────────────────────── */
const LOGIN_URL = '/index';           // existing users sign in here; index() clears the session on arrival

const SessionExpired = () => createPortal(
  <div style={se.overlay} role="dialog" aria-modal="true" aria-label="Session expired">
    <div style={se.card}>
      <div style={se.title}>Your session has expired</div>
      <p style={se.body}>
        You&apos;ve been signed out, so nothing on this page can be saved. Sign in again to carry on.
      </p>
      <button type="button" style={se.btn} onClick={() => { window.location.href = LOGIN_URL; }} autoFocus>
        Sign in again
      </button>
    </div>
  </div>,
  document.body,
);

const se = {
  overlay: { position: 'fixed', inset: 0, zIndex: 10080, display: 'flex', alignItems: 'center',
             justifyContent: 'center', padding: 16, background: 'rgba(3,7,18,.72)', backdropFilter: 'blur(3px)' },
  card:    { width: '100%', maxWidth: 380, borderRadius: 14, padding: '22px 20px', textAlign: 'center',
             background: '#0f172a', border: '1px solid rgba(255,255,255,.14)', boxShadow: '0 24px 60px rgba(0,0,0,.55)' },
  title:   { font: '700 17px/1.3 system-ui', color: '#e5edf7', marginBottom: 8 },
  body:    { font: '400 13.5px/1.5 system-ui', color: '#94a3b8', margin: '0 0 18px' },
  btn:     { width: '100%', padding: '11px 16px', borderRadius: 10, border: 0, cursor: 'pointer',
             background: '#22d3ee', color: '#0b1220', font: '700 14px system-ui' },
};

/* ─────────────────────────────────────────────────────────────
   Shell — modal + dock pill
   ───────────────────────────────────────────────────────────── */
const MessageCenter = () => {
  useStylesheet(cssUrl('messageCenter.css'));
  const { user } = useUser();
  const mc = useMessages();
  // Without this the bad-mount failure is "Cannot destructure property 'panel' of null",
  // which says nothing about the cause. MessageBell guards the same way (it renders null).
  if (!mc) throw new Error('MessageCenter must be rendered inside <MessagesProvider> — see App.js');
  const accent = getRoleAccent(user);
  const { panel, view, unread, unreadReads, close, dock, open, goInbox, startCompose, error, setError, loggedIn, me, threads, openThread, refreshThreads, authFailed } = mc;
  const myName = me?.name || user?.userName || '';
  const myPhoto = me?.photo || user?.headshot || '';

  useEffect(() => {
    if (panel !== 'open') return undefined;
    const onKey = (e) => e.key === 'Escape' && dock();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [panel, dock]);

  // ── arrival toast ──
  // Announced ONCE per message, keyed on its Airtable record id and remembered in
  // localStorage. So a message that landed while you were signed out still announces itself
  // the first time you come back, and never announces itself again — which a count-delta
  // can't do, and which a flag on the message row can't do either: a broadcast to 50 people
  // is a single Messages record (see send_messages), so per-recipient state has to live
  // per-recipient. Per browser by design — no cross-device sync wanted.
  const [bump, setBump] = useState(false);
  const [arrival, setArrival] = useState(null);           // { n, at, latest: { t, m } | null }
  const prevUnread = useRef(null);
  const panelRef = useRef(panel); panelRef.current = panel;

  const toastedKey = `mc-toasted-${user?.id || 'anon'}`;  // per login record id — shared machines don't bleed
  const toastedRef = useRef(new Set());
  const toastedKeyRef = useRef('');
  useEffect(() => {                                       // load this user's memory
    toastedKeyRef.current = toastedKey;
    try { toastedRef.current = new Set(JSON.parse(window.localStorage.getItem(toastedKey) || '[]')); }
    catch (e) { toastedRef.current = new Set(); }         // private window / storage blocked
  }, [toastedKey]);

  const rememberToasted = (ids) => {
    ids.forEach((i) => toastedRef.current.add(i));
    const keep = [...toastedRef.current].slice(-TOAST_MEMORY);
    toastedRef.current = new Set(keep);
    try { window.localStorage.setItem(toastedKeyRef.current, JSON.stringify(keep)); }
    catch (e) { /* storage unavailable — falls back to in-memory for this session */ }
  };

  // The unread poll only returns a count, so fetch the thread bodies when there is something
  // we might not have announced yet: the first reading after a login, or a count that grew.
  useEffect(() => {
    if (!unreadReads) { prevUnread.current = null; return undefined; }
    const prev = prevUnread.current;
    prevUnread.current = unread;
    if (!unread) return undefined;
    if (prev !== null && unread <= prev) return undefined;
    refreshThreads(true);
    return undefined;
  }, [unread, unreadReads, refreshThreads]);

  // …then the thread data decides what to announce. Anything unread we haven't toasted yet
  // gets marked here whether or not it is shown, so opening the panel counts as announced.
  useEffect(() => {
    const fresh = [];
    let newest = null;
    for (const t of threads) {
      for (const m of t.messages) {
        if (!m.unread || toastedRef.current.has(m.id)) continue;
        fresh.push(m.id);
        if (!newest || m.createdAt > newest.m.createdAt) newest = { t, m };
      }
    }
    if (!fresh.length) return undefined;
    rememberToasted(fresh);
    setBump(true);
    const t = setTimeout(() => setBump(false), 1500);
    if (panelRef.current !== 'open') setArrival({ n: fresh.length, at: Date.now(), latest: newest });
    return () => clearTimeout(t);
  }, [threads]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!arrival) return undefined;
    const t = setTimeout(() => setArrival(null), 5600);   // 5 s visible + fade
    return () => clearTimeout(t);
  }, [arrival]);
  useEffect(() => { if (panel === 'open') setArrival(null); }, [panel]);   // opened it — toast has done its job

  if (!loggedIn) return null;

  // A dead Flask session breaks every call in the app, not just the bell — so this wins
  // over the panel, the dock and the toast. `authFailed` is only set on a real 401
  // (see is401 in MessagesContext), never on a network blip.
  if (authFailed) return <SessionExpired />;

  // the toast carries its own subject, captured when it fired
  const latest = arrival?.latest || null;
  const toast = arrival ? createPortal(
    <button
      type="button"
      className="mc-arrive"
      onClick={() => { setArrival(null); if (latest) openThread(latest.t.key); else { goInbox(); open(); } }}
      aria-live="polite"
    >
      <span className="mc-arrive-dot" />
      <span className="mc-arrive-text">
        {/* one message names its sender; several say how many, and name the most recent —
            signing back in to three unread shouldn't read as a single message from one person */}
        <strong>{arrival.n > 1
          ? `${arrival.n} new messages`
          : `New message from ${latest?.m.fromName || 'someone'}`}</strong>
        {latest && (
          <span className="mc-arrive-sub">
            {arrival.n > 1 ? `Latest from ${latest.m.fromName || 'someone'} · ` : ''}
            {latest.t.title}
            {latest.m.text ? ` — ${latest.m.text.split(/\s+/).slice(0, 12).join(' ')}${latest.m.text.split(/\s+/).length > 12 ? '…' : ''}` : ''}
          </span>
        )}
      </span>
    </button>,
    document.body,
  ) : null;

  if (panel === 'closed') return toast;

  if (panel === 'docked') {
    return createPortal(
      <>
      {toast}
      <button type="button" className={`mc-dock ${bump ? 'mc-dock-bump' : ''}`} style={{ borderColor: `${accent}66` }} onClick={open} aria-label="Open messages">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
        <span>Messages</span>
        {unread > 0 && <span className="mc-badge" style={{ background: accent }}>{unread}</span>}
        <svg className="mc-dock-up" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
      </button>
      </>,
      document.body,
    );
  }

  return createPortal(
    <div className="mc-overlay" onMouseDown={(e) => e.target === e.currentTarget && dock()}>
      <div className="mc-modal" role="dialog" aria-modal="true" aria-label="Messages" style={{ '--mc-accent': accent }}>
        <div className="mc-head">
          <div className="mc-title">
            <Avatar name={myName} photo={myPhoto} accent={accent} size={30} />
            <span className="mc-title-text">Messages</span>{unread > 0 && <span className="mc-badge" style={{ background: accent }}>{unread}</span>}
          </div>
          <div className="mc-head-tabs">
            <button type="button" className={`mc-tab ${view === 'inbox' || view === 'thread' ? 'on' : ''}`} style={view !== 'compose' ? { color: accent, borderColor: accent } : undefined} onClick={goInbox}>Inbox</button>
            <button type="button" className={`mc-tab ${view === 'compose' ? 'on' : ''}`} style={view === 'compose' ? { color: accent, borderColor: accent } : undefined} onClick={() => startCompose()}>New message</button>
          </div>
          <div className="mc-head-btns">
            <button type="button" className="mc-icon-btn" title="Minimise — keeps this open while you move around the app" aria-label="Minimise" onClick={dock}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            <button type="button" className="mc-icon-btn" title="Close" aria-label="Close" onClick={close}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>
        </div>
        <div className="mc-body">
          {view === 'inbox' && <Inbox accent={accent} />}
          {view === 'thread' && <Thread accent={accent} />}
          {view === 'compose' && <Compose accent={accent} />}
        </div>
        {error && <div className="mc-toast" onClick={() => setError('')}>{error}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default MessageCenter;

/* ─────────────────────────────────────────────────────────────
   Bell — drop into TabBar (right corner). Safe without a provider.
   ───────────────────────────────────────────────────────────── */
export const MessageBell = ({ accent, size = 20, className = '', label = null }) => {
  const mc = useMessages();
  const registerBell = mc?.registerBell;
  useEffect(() => (registerBell ? registerBell() : undefined), [registerBell]);   // mounted bell = poll; unmounted = stop
  if (!mc || !mc.loggedIn) return null;
  const { unread, toggle, panel } = mc;
  /* `label` turns the bare icon into a labelled pill (Global Messages). The
     pill is tinted with the caller's role accent — the same getRoleAccent()
     colour the AvatarRing beside it uses — handed to CSS as --mc-accent so
     one variable drives the border, glyph, badge and tint. Callers that pass
     no label render exactly as they always did. */
  const glyph = (
    <>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={panel === 'open' && !label ? accent : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {unread > 0 && <span className="mc-bell-badge" style={label ? undefined : { background: accent }}>{unread > 99 ? '99+' : unread}</span>}
    </>
  );
  return (
    <button type="button" className={`mc-bell ${label ? 'mc-bell-pill' : ''} ${panel === 'open' ? 'on' : ''} ${className}`}
      style={label && accent ? { '--mc-accent': accent } : undefined}
      onClick={toggle} aria-label={unread ? `${unread} unread messages` : 'Messages'} title={label || 'Messages'}>
      {label && <span className="mc-bell-label">{label}</span>}
      {label ? <span className="mc-bell-ico">{glyph}</span> : glyph}
    </button>
  );
};